"use client";

/**
 * Last-resort boundary: catches a throw in the root layout itself, which is the one
 * case app/error.tsx cannot handle.
 *
 * It **replaces** the root layout, so it must emit its own <html>/<body> — and, less
 * obviously, `globals.css` is not applied, because that stylesheet is imported by the
 * layout this file is standing in for. Every Tailwind class would therefore be inert.
 * So the styling here is inline, deliberately, following the same reasoning as
 * app/reports/share/[token]/page.tsx. Do not "tidy" these into Tailwind classes.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          background: "#ffffff",
          color: "#0f172a",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "32rem",
            border: "1px solid #e2e8f0",
            borderRadius: "0.75rem",
            padding: "1.5rem",
          }}
        >
          <h1 style={{ fontSize: "1.125rem", fontWeight: 600, margin: "0 0 0.75rem" }}>
            Something went wrong
          </h1>
          {error.message ? (
            <p style={{ fontSize: "0.875rem", color: "#b91c1c", wordBreak: "break-word" }}>
              {error.message}
            </p>
          ) : null}
          {error.digest ? (
            <p
              style={{
                fontSize: "0.75rem",
                color: "#64748b",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              digest: {error.digest}
            </p>
          ) : null}
          <p style={{ fontSize: "0.75rem", color: "#64748b" }}>
            The application failed to start. Nothing you were viewing has been changed.
          </p>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
            <button
              onClick={reset}
              style={{
                fontSize: "0.875rem",
                padding: "0.375rem 0.75rem",
                borderRadius: "0.375rem",
                border: "1px solid #cbd5e1",
                background: "transparent",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            {/*
              A plain anchor, not next/link, on purpose: this boundary catches a failure in
              the root layout, so client-side routing is one of the things that may be
              broken. A full document load is the only navigation guaranteed to work here.
              eslint-disable-next-line @next/next/no-html-link-for-pages
            */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/dashboard/sites"
              style={{
                fontSize: "0.875rem",
                padding: "0.375rem 0.75rem",
                borderRadius: "0.375rem",
                color: "#334155",
                textDecoration: "none",
                alignSelf: "center",
              }}
            >
              Back to sites
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
